<script lang="ts" setup>
  import { i18n } from '@/plugins/i18n';
  import { Card, Icon } from '@skillstown/leap';
  import enData from './ENFaq.json';
  import nlData from './NLFaq.json';
  import { BlobClient } from '@/data/BlobClient';
  import { MimeType } from '~~/data/enums/MimeType';

  interface Faq {
    sections: FaqSection[];
  }

  interface FaqSection {
    title: string;
    questionsAndAnswers: QuestionAndAnswer[];
  }

  interface QuestionAndAnswer {
    question: string;
    answer: string;
    [key: string]: any;
  }

  const { cardPadding } = useLeapCustomizations();

  const currentLocale = computed(() => {
    return i18n.global.locale.value;
  });

  const faqNlObj = ref(nlData) as Ref<Faq>;

  const faqEnObj = ref(enData) as Ref<Faq>;

  const faqList = computed(() => {
    return currentLocale.value === 'nl' ? faqNlObj.value : faqEnObj.value;
  });

  const faqData = computed(() => {
    faqList.value.sections.forEach((item) => {
      item.questionsAndAnswers.forEach((section) => {
        section.isActive = false;
      });
    });
    return faqList.value;
  });

  function isIosDisplayQuestion(question: string): boolean {
    return (
      question === `Why can't I see content on IOS devices?` ||
      question === `Waarom zie ik geen inhoud op mijn IOS apparaat?`
    );
  }

  async function onClickDownloadIosInstructions(): Promise<void> {
    await new BlobClient('FAQ Cross site tracking.pdf').fetchAndDownloadFromPublicDirectory(
      'FAQ Cross site tracking.pdf',
      MimeType.PDF
    );
  }
</script>

<template>
  <ModalBase
    :title="$t('frequently_asked_questions')"
    :isFullScreen="true"
    :removeFooter="true"
  >
    <!-- :onClose="onClickClose" -->
    <!-- <div
      class="faq-title"
      data-testid="faq-title"
    >
      <NavigationTitle>
        <template v-slot:title>{{ $t('frequently_asked_questions') }}</template>
      </NavigationTitle>
    </div> -->
    <div>
      <!-- eslint-disable vue/require-v-for-key -->
      <div
        class="faq-container"
        :padding="cardPadding"
      >
        <div
          v-for="item of faqData.sections"
          class="faq-container__section"
        >
          <h3
            class="section__title font-title-m"
            data-testid="faq-section-title"
          >
            {{ item.title }}
          </h3>
          <div
            v-for="section of item.questionsAndAnswers"
            class="section__item"
            :class="section.isActive ? 'section__item__active' : ''"
          >
            <div
              class="item__question"
              data-testid="faq-question"
              @click="section.isActive = !section.isActive"
            >
              <p class="font-title-xs pr-5">
                {{ section.question }}
              </p>
              <Icon
                :icon="section.isActive ? 'dirUp' : 'dirDown'"
                class="question__icon"
                :class="section.isActive ? 'question__icon--active' : ''"
                variant="ghost"
                size="m"
                :color="section.isActive ? 'brand' : ''"
              />
            </div>
            <div
              v-if="section.isActive"
              data-testid="faq-answer"
              class="item__answer font-body-xxs"
            >
              {{ section.answer }}
              <div
                v-if="isIosDisplayQuestion(section.question)"
                class="mt-6 flex cursor-pointer items-center"
                @click="onClickDownloadIosInstructions"
              >
                <span>{{ $t('download_step_by_step_instructions') }}</span>
                <Icon
                  icon="download"
                  class="mx-2 cursor-pointer"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <!-- eslint-enable vue/require-v-for-key -->
    </div>
  </ModalBase>
</template>

<style scoped lang="postcss">
  .faq-container {
    column-count: 2;
    column-gap: 40px;
    width: 100%;
    .faq-container__section {
      display: inline-block;
      width: 100%;
      margin-bottom: 20px;
      .section__title {
        @apply mb-2 text-lg font-medium;
        color: var(--color-brand);
      }
      .section__item {
        @apply flex flex-col pl-2;
        border-bottom: 1px solid var(--color-grey-soft);
        &:last-child {
          border: none;
        }
        .item__question {
          @apply flex items-center pb-3 pr-2 pt-3 text-sm font-medium;
          justify-content: space-between;
          align-items: center;
          color: #44576a;
          .question__icon {
            font-weight: bold;
            height: 24px;
            width: 24px;
            :deep(svg) {
              padding: 0;
            }
            &--active {
              background-color: white;
            }
          }
        }
        .item__answer {
          @apply text-xs font-normal;
          color: #57738e;
          width: 90%;
        }
        &__active {
          background-color: #f4f7fb;
          padding: 27px 0px 27px 24px;
          .faq-item_question {
            border-bottom: none;
            margin: 0;
            padding: 0;
            margin-bottom: 25px;
          }
        }
      }
    }
  }
</style>
